import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import type { ReactNode } from "react";
import { useState } from "react";

/**
 * The image shoppers see for the battle loser prize, as a URL with a live preview. Shared by
 * the platform default (`/battles`) and an environment's override (Storefront → Battles): the
 * two differ only in whether an empty URL may be saved (the platform default may be cleared;
 * an environment goes back to the default through `footer` instead). Remount with `key` when
 * the saved value changes, like the shipping editor.
 */
export function LoserPrizeImageForm({
  imageUrl,
  canEdit,
  saving,
  allowEmpty = false,
  onSave,
  footer,
}: {
  imageUrl: string;
  canEdit: boolean;
  saving: boolean;
  /** Whether saving with the field blank is a valid save (clears the image). */
  allowEmpty?: boolean;
  onSave: (imageUrl: string) => void;
  footer?: ReactNode;
}) {
  const [draft, setDraft] = useState(imageUrl);
  const trimmed = draft.trim();
  const dirty = trimmed !== imageUrl;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!allowEmpty && trimmed === "") return;
    onSave(trimmed);
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <LoserPrizePreview imageUrl={trimmed} className="size-24 shrink-0 rounded-md" />
        <div className="grid flex-1 gap-2">
          <Label htmlFor="loser-prize-image-url">Image URL</Label>
          <Input
            id="loser-prize-image-url"
            type="url"
            value={draft}
            disabled={!canEdit}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://cdn.example.com/mystery-bulk-card.png"
          />
          <p className="text-xs text-muted-foreground">
            Shown on the battle page and in the shopper's inventory.
            {allowEmpty ? " Leave it blank for the placeholder." : ""}
          </p>
        </div>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving || !dirty || (!allowEmpty && trimmed === "")}>
            {saving ? "Saving…" : "Save image"}
          </Button>
          {footer}
        </div>
      )}
    </form>
  );
}

/** The prize's artwork as the storefront draws it, placeholder included, so staff see what shoppers see. */
export function LoserPrizePreview({
  imageUrl,
  className,
}: {
  imageUrl: string;
  className?: string;
}) {
  return (
    <BoxImage
      src={imageUrl}
      className={`${className ?? ""} object-cover`}
      fallback={<span className="text-2xl opacity-40">🎁</span>}
    />
  );
}
